import { useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import fetcher from '~/src/lib/fetcher'
import { GeoData } from '~/src/lib/geo'
import useUserData from '~/src/hooks/useUserData'
import { DUPE_API_URL } from '@dupe/common/constants'
import useAuthUser from '~/src/hooks/useAuthUser'

// Avoid multiple instances of hook from updating simultaneously
let globalIsUpdating = false

const LOADING_DELAY = 1500 // 1.5 seconds

const useGeo = () => {
  const [isLoading, setIsLoading] = useState(true)

  const {
    data,
    error,
    isLoading: isLoadingSWR,
  } = useSWR<GeoData | undefined | null>('/api/geo', fetcher, {
    revalidateOnFocus: false,
  })

  const { userData } = useUserData()
  const { authUser, loading: isAuthUserLoading } = useAuthUser()
  const hasUpdated = useRef(false)

  const detectedCountryCode = (data?.country || '').toLowerCase()
  const currentCountryCode = userData?.countryCode
  const hasUser = !!userData?.id
  const [isUpdating, setIsUpdating] = useState(false)

  // Update country code when user data changes
  useEffect(() => {
    if (globalIsUpdating) return
    if (!hasUser) return
    if (!detectedCountryCode) return
    if (isAuthUserLoading) return
    if (!authUser) return
    if (isUpdating) return // Prevent concurrent requests
    if (
      !!currentCountryCode &&
      !!detectedCountryCode &&
      currentCountryCode === detectedCountryCode
    )
      return
    if (hasUpdated.current) return
    globalIsUpdating = true
    ;(async () => {
      try {
        // Set loading state immediately to prevent race conditions
        setIsUpdating(true)
        hasUpdated.current = true

        await fetcher(`${DUPE_API_URL}/api/user/update-country-code`, {
          method: 'POST',
          authUser,
        })
      } catch (_error) {
        // Reset hasUpdated on error so we can retry
        hasUpdated.current = false
      } finally {
        globalIsUpdating = false
        setIsUpdating(false)
      }
    })()
  }, [
    detectedCountryCode,
    currentCountryCode,
    hasUser,
    authUser,
    isAuthUserLoading,
    isUpdating,
  ])

  // set isLoading to true when isLoadingSWR is true
  useEffect(() => {
    setIsLoading(isLoadingSWR)
  }, [isLoadingSWR])

  // set isLoading to false after LOADING_DELAY
  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (isLoading) {
      timeout = setTimeout(() => {
        setIsLoading(false)
      }, LOADING_DELAY)
    }
    return () => timeout && clearTimeout(timeout)
  }, [isLoading])

  return {
    data,
    isLoading,
    isError: error,
  }
}

export default useGeo
